const browserOrigin =
  typeof window === "undefined" ? "http://localhost:3400" : window.location.origin;
const localApiOrigin =
  typeof window !== "undefined" && window.location.port === "3401"
    ? `${window.location.protocol}//${window.location.hostname}:3400`
    : browserOrigin;

export const API_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? localApiOrigin;

export type Game = "minecraft_java";

export interface SessionUser {
  id: string;
  username: string;
  role: string;
  email?: string;
  balance?: number;
  serverLimit?: number;
  lastDailyBonusAt?: string | null;
  lastLoginAt?: string | null;
  createdAt?: string;
}

export interface User extends SessionUser {
  email: string;
  balance: number;
}

export interface AuthSession {
  accessToken: string;
  user: User;
}

export interface CreditHistoryEntry {
  id: string | number;
  amount: number;
  source: string;
  createdAt: string;
}

export interface CreditSummary {
  balance: number;
  serverLimit: number;
  lastDailyBonusAt: string | null;
  history?: CreditHistoryEntry[];
}

export interface CreditTopUpConfig {
  enabled: boolean;
  minRubles: number;
  maxRubles: number;
}

export interface CreditTopUp {
  id: string;
  amountRubles: number;
  credits: number;
  bonusPercent: number;
  status: string;
  checkoutUrl: string | null;
  createdAt: string;
  creditedAt: string | null;
}

export interface AdminPayment extends CreditTopUp {
  userId: string;
  username: string;
  email: string;
  providerId: string | null;
  checkedAt: string | null;
  isFinal: boolean;
}

export interface GameServer {
  queuePosition?: number | null;
  id: string;
  name: string;
  game: Game;
  core: string;
  version: string;
  javaVersion: number;
  status: string;
  memLimitMb: number;
  diskLimitMb: number;
  playerLimit: number;
  cpuLimitPercent: number;
  backupsEnabled: boolean;
  backupSlots?: number;
  joinCode: string;
  joinAddress: string;
  lastError: string | null;
  hourlyCost?: number;
  createdAt: string;
  billingSettlementPending?: boolean;
}

export interface ServerDetails extends GameServer {
  queuePosition: number | null;
  events: Array<{ id: number; type: string; message: string; createdAt: string }>;
}

export interface AdminNode {
  id: string;
  name: string;
  region: string;
  hostname: string;
  publicIp: string;
  status: string;
  heartbeatAgeSec: number | null;
  capacityContainers: number;
  maxRunningMemoryMb: number | null;
  allocatedRunningMemoryMb: number;
  memTotalMb: number | null;
  lastHeartbeatAt: string | null;
  createdAt: string;
}

export interface AdminInventoryQuery {
  query?: string;
  cursor?: string | null;
  limit?: number;
  status?: string;
  excludeId?: string;
}
function inventoryQuery(options: AdminInventoryQuery = {}) {
  const query = new URLSearchParams({ limit: String(options.limit ?? 10) });
  for (const [key, value] of Object.entries(options)) {
    if (value !== undefined && value !== null && value !== "") {
      query.set(key, String(value));
    }
  }
  return query.toString();
}

export interface NodeServer {
  id: string;
  name: string;
  status: string;
  ownerId: string;
  ownerUsername?: string;
  updatedAt: string;
  s3Configured?: boolean;
}

export interface NodeAuditEvent {
  id: number;
  nodeId: string | null;
  action: string;
  meta: Record<string, unknown> | null;
  createdAt: string;
  actorUsername: string | null;
}

export interface AdminOverview {
  users: number;
  servers: number;
  nodes: number;
  backups: number;
  creditsInCirculation: number;
  nodesByStatus: { online: number; offline: number };
  serversByStatus: { running: number; queued: number; failed: number };
  s3Configured?: boolean;
}
export interface AdminLoadedServer {
  id: string;
  name: string;
  ownerId: string;
  ownerUsername: string;
  nodeId: string | null;
  nodeName: string;
  cpuPercent: number;
  cpuLimitPercent: number;
  cpuUsagePercent: number;
  memoryUsedMb: number;
  memoryLimitMb: number;
  memUsagePercent: number;
  diskUsedMb: number;
  diskLimitMb: number;
}

export interface AdminStats {
  days: number;
  userRegistrations: Array<{ day: string; count: number }>;
  serversCreated: Array<{ day: string; count: number }>;
  creditsInflow: Array<{ day: string; amount: number }>;
  creditsOutflow: Array<{ day: string; amount: number }>;
  serversByGame: Array<{ game: string; count: number }>;
  serversByCore: Array<{ core: string; count: number }>;
  nodesUsage: Array<{
    id: string;
    name: string;
    status: string;
    capacity: number;
    memTotalMb: number;
    serverCount: number;
    allocatedMemMb: number;
  }>;
  loadedServers: AdminLoadedServer[];
}

export interface AdminUser {
  id: string;
  email: string;
  username: string;
  role: string;
  balance: number;
  serverLimit: number;
  serverCount: number;
  lastLoginAt: string | null;
  createdAt: string;
}

export interface AdminUserLedgerItem {
  id: string | number;
  amount: number;
  balanceAfter: number;
  source: string;
  refId: string | null;
  meta: unknown;
  createdAt: string;
}

export interface AdminPage<T> {
  items: T[];
  nextCursor: string | null;
}
export type AdminUserLedgerResult = AdminPage<AdminUserLedgerItem>;

export interface AdminUserDetails {
  user: AdminUser & { lastDailyBonusAt?: string | null };
  servers: Array<
    AdminServer & { diskLimitMb: number; playerLimit: number; cpuLimitPercent: number }
  >;
  ledger: AdminUserLedgerItem[];
  serversNextCursor: string | null;
  ledgerNextCursor: string | null;
}

export interface AdminServer {
  id: string;
  name: string;
  game: string;
  core: string;
  version: string;
  status: string;
  ownerId: string;
  ownerUsername: string;
  nodeId: string | null;
  nodeName: string | null;
  nodeStatus?: string | null;
  nodeIp?: string | null;
  backupHealth?: {
    status: string;
    purpose: string | null;
    destination: string | null;
    lastSuccessAt: string | null;
    updatedAt: string | null;
  };
  joinCode?: string;
  joinAddress?: string;
  backend?: { host: string; port: number } | null;
  backendAddress?: string | null;
  hostPort: number | null;
  memLimitMb: number;
  backupsEnabled: boolean;
  backupSlots?: number;
  lastError: string | null;
  createdAt: string;
  updatedAt: string;
  s3Configured?: boolean;
}

export interface AdminAuditEvent {
  id: number;
  action: string;
  targetType: string;
  targetId: string | null;
  meta: Record<string, unknown> | null;
  createdAt: string;
  actorUsername: string | null;
}

export interface Backup {
  id: string;
  serverId: string;
  sizeBytes: number | null;
  checksumSha256: string | null;
  status: string;
  error: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface MinecraftPlayer {
  name: string;
  uuid?: string;
  latencyMs?: number;
}

export interface ServerTelemetry {
  serverId: string;
  capturedAt: string;
  status: string;
  cpuPercent: number | null;
  cpuLimitPercent: number | null;
  memoryUsedMb: number | null;
  memoryLimitMb: number | null;
  diskUsedMb: number | null;
  diskLimitMb: number | null;
  playersOnline: number;
  playersMax: number | null;
  players: MinecraftPlayer[];
  error?: string;
  staleAfterSec: number;
}

export interface ServerPlayers {
  serverId: string;
  capturedAt: string | null;
  players: MinecraftPlayer[];
  playersOnline: number;
  playersMax: number | null;
  stale: boolean;
}

export interface CreateServerInput {
  name: string;
  game: Game;
  core: string;
  version: string;
  javaVersion?: number;
  memLimitMb: number;
}

export interface ServerCapacity {
  available: boolean;
  capturedAt: string;
  ageMs: number;
  stale: boolean;
}

export interface UpdateServerSettingsInput {
  name?: string;
  core?: string;
  version?: string;
  javaVersion?: number;
  confirmWipe?: boolean;
  reinstall?: boolean;
}

export interface FileRevision {
  mtimeMs: number;
  size: number;
  sha256: string;
}

export interface FileEntry {
  name: string;
  path: string;
  kind: "file" | "directory";
  size: number;
  mtimeMs: number;
  editable?: boolean;
}

export interface FileListResponse {
  path: string;
  entries: FileEntry[];
}

export interface FileContentResponse {
  path: string;
  offset: number;
  content: string;
  size: number;
  eof: boolean;
  revision: FileRevision;
}

export interface FileDownloadResponse {
  path: string;
  size: number;
  content: string;
}

export interface FileWriteResponse {
  path: string;
  size: number;
  revision: FileRevision;
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function request<T>(path: string, options: RequestInit = {}, token?: string): Promise<T> {
  const response = await fetch(`${API_URL}${path}`, {
    ...options,
    headers: {
      ...(options.body && !(typeof FormData !== "undefined" && options.body instanceof FormData)
        ? { "content-type": "application/json" }
        : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  }).catch((error: unknown) => {
    const hasErrorName = typeof error === "object" && error !== null && "name" in error;
    const aborted = hasErrorName && error.name === "AbortError";
    if (aborted) {
      throw error;
    }
    throw new ApiError(
      "Не удалось связаться с панелью. Проверьте подключение к интернету. Если отправляли действие, проверьте состояние сервера перед повторной попыткой.",
      0,
    );
  });
  const body: unknown = await response.json().catch(() => ({}));

  if (!response.ok) {
    if (response.status === 401 && token && typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("mars:unauthorized", { detail: { token } }));
    }
    const rawMessage =
      typeof body === "object" && body !== null && "message" in body ? body.message : undefined;
    const message =
      typeof rawMessage === "string"
        ? rawMessage
        : Array.isArray(rawMessage) && rawMessage.every((item: unknown) => typeof item === "string")
          ? rawMessage.join(". ")
          : undefined;
    const fallback =
      response.status >= 500
        ? "Панель временно не может выполнить запрос. Проверьте состояние сервера перед повторной попыткой."
        : response.status === 429
          ? "Слишком много запросов. Подождите немного и повторите действие."
          : response.status === 401
            ? "Сессия завершилась. Войдите в аккаунт снова."
            : `Не удалось выполнить запрос (код ${response.status}).`;
    throw new ApiError(
      response.status >= 500 || response.status === 429 || response.status === 401
        ? fallback
        : (message ?? fallback),
      response.status,
      typeof body === "object" && body !== null && "code" in body && typeof body.code === "string"
        ? body.code
        : undefined,
    );
  }

  return body as T;
}

const mutation = (method: "POST" | "DELETE" | "PUT" | "PATCH", body?: unknown): RequestInit => ({
  method,
  ...(body === undefined ? {} : { body: JSON.stringify(body) }),
});

export type ServerResource = "ram" | "disk" | "players" | "cpu";

export interface AiTask {
  id: string;
  prompt: string;
  answer: string | null;
  status: "running" | "awaiting_confirmation" | "cancelling" | "completed" | "failed" | "cancelled";
  confirmation: string | null;
  createdAt: string;
  actions: Array<{ tool: string; target: string | null; result: string }>;
}
export interface AiState {
  configured: boolean;
  quota: {
    inputTokensRemaining: number;
    inputTokensLimit: number;
    outputTokensRemaining: number;
    outputTokensLimit: number;
    resetsAt: string;
  };
  tasks: AiTask[];
}

export const api = {
  aiState: (token: string, serverId: string, signal?: AbortSignal) =>
    request<AiState>(`/api/servers/${serverId}/ai`, { signal }, token),
  aiSend: (token: string, serverId: string, message: string) =>
    request<{ id: string }>(`/api/servers/${serverId}/ai`, mutation("POST", { message }), token),
  aiClear: (token: string, serverId: string) =>
    request<{ ok: boolean }>(`/api/servers/${serverId}/ai`, mutation("DELETE"), token),
  aiCancel: (token: string, serverId: string, taskId: string) =>
    request<{ ok: boolean }>(
      `/api/servers/${serverId}/ai/${taskId}/cancel`,
      mutation("POST"),
      token,
    ),
  aiConfirm: (token: string, serverId: string, taskId: string) =>
    request<{ ok: boolean }>(
      `/api/servers/${serverId}/ai/${taskId}/confirm`,
      mutation("POST"),
      token,
    ),
  register: (body: {
    email: string;
    username: string;
    password: string;
    turnstileToken?: string;
  }) => request<AuthSession>("/api/auth/register", mutation("POST", body)),
  login: (body: { email: string; password: string; turnstileToken?: string }) =>
    request<AuthSession>("/api/auth/login", mutation("POST", body)),
  me: (token: string, signal?: AbortSignal) =>
    request<SessionUser>("/api/auth/me", { signal }, token),
  updateProfile: (token: string, username: string) =>
    request<void>("/api/auth/profile", mutation("PATCH", { username }), token),
  changePassword: (token: string, currentPassword: string, newPassword: string) =>
    request<void>("/api/auth/password", mutation("POST", { currentPassword, newPassword }), token),
  logout: (token: string) => request<void>("/api/auth/logout", mutation("POST"), token),
  balance: (token: string, signal?: AbortSignal) =>
    request<CreditSummary>("/api/credits/balance", { signal }, token),
  topUpConfig: (token: string, signal?: AbortSignal) =>
    request<CreditTopUpConfig>("/api/credits/top-ups/config", { signal }, token),
  topUps: (token: string, signal?: AbortSignal) =>
    request<CreditTopUp[]>("/api/credits/top-ups", { signal }, token),
  createTopUp: (
    token: string,
    body: { amountRubles: number; idempotencyKey: string },
    signal?: AbortSignal,
  ) => request<CreditTopUp>("/api/credits/top-ups", { ...mutation("POST", body), signal }, token),
  checkTopUp: (token: string, id: string, signal?: AbortSignal) =>
    request<CreditTopUp>(
      `/api/credits/top-ups/${encodeURIComponent(id)}/check`,
      { ...mutation("POST", {}), signal },
      token,
    ),
  purchaseServerSlot: (token: string) =>
    request<{ balance: number; serverLimit: number; cost: number }>(
      "/api/credits/server-slot",
      mutation("POST", {}),
      token,
    ),
  dailyBonus: (token: string) =>
    request<{ balance: number; amount: number }>(
      "/api/credits/daily-bonus",
      mutation("POST", {}),
      token,
    ),
  capacity: (token: string, signal?: AbortSignal) =>
    request<ServerCapacity>("/api/servers/capacity", { signal }, token),
  servers: (token: string, signal?: AbortSignal) =>
    request<GameServer[]>("/api/servers", { signal }, token),
  server: (token: string, id: string, signal?: AbortSignal) =>
    request<ServerDetails>(`/api/servers/${id}`, { signal }, token),
  consoleHistory: (token: string, id: string, signal?: AbortSignal) =>
    request<string[]>(`/api/servers/${id}/console`, { signal }, token),
  coreVersions: (token: string, core: string, signal?: AbortSignal) =>
    request<{ core: string; versions: string[] }>(
      `/api/servers/versions?core=${encodeURIComponent(core)}`,
      { signal },
      token,
    ),
  createServer: (token: string, body: CreateServerInput) =>
    request<GameServer>("/api/servers", mutation("POST", body), token),
  updateServerSettings: (token: string, id: string, body: UpdateServerSettingsInput) =>
    request<GameServer>(`/api/servers/${id}/settings`, mutation("PUT", body), token),
  pluginSearch: (
    token: string,
    id: string,
    query: string,
    source = "modrinth",
    offset = 0,
    limit = 10,
    signal?: AbortSignal,
  ) =>
    request<{
      source: string;
      directory: string;
      results: Array<{
        id: string;
        name: string;
        description: string;
        slug: string;
        iconUrl: string | null;
      }>;
      total?: number;
      offset?: number;
      limit?: number;
    }>(
      `/api/servers/${id}/plugins/search?query=${encodeURIComponent(query)}&source=${encodeURIComponent(source)}&offset=${offset}&limit=${limit}`,
      { signal },
      token,
    ),
  pluginVersions: (
    token: string,
    id: string,
    projectId: string,
    source = "modrinth",
    includeIncompatible = false,
  ) =>
    request<{
      source: string;
      versions: Array<{
        id: string;
        name: string;
        gameVersions: string[];
        loaders?: string[];
        files: Array<{ url: string; filename: string; primary?: boolean }>;
        compatible?: boolean;
      }>;
    }>(
      `/api/servers/${id}/plugins/versions?projectId=${encodeURIComponent(projectId)}&source=${encodeURIComponent(source)}&includeIncompatible=${includeIncompatible ? "true" : "false"}`,
      {},
      token,
    ),
  plugins: (token: string, id: string) =>
    request<{ directory: string; entries: Array<{ name: string; size: number; mtimeMs: number }> }>(
      `/api/servers/${id}/plugins`,
      {},
      token,
    ),
  installPlugin: (
    token: string,
    id: string,
    projectId: string,
    versionId: string,
    source = "modrinth",
    force = false,
  ) =>
    request<{ path: string }>(
      `/api/servers/${id}/plugins`,
      mutation("POST", { projectId, versionId, source, force }),
      token,
    ),
  deletePlugin: (token: string, id: string, filename: string) =>
    request<{ path: string }>(
      `/api/servers/${id}/plugins`,
      mutation("DELETE", { filename }),
      token,
    ),
  startServer: (token: string, id: string) =>
    request<{ ok: boolean }>(`/api/servers/${id}/start`, mutation("POST", {}), token),
  stopServer: (token: string, id: string) =>
    request<{ ok: boolean }>(`/api/servers/${id}/stop`, mutation("POST", {}), token),
  restartServer: (token: string, id: string) =>
    request<{ ok: boolean }>(`/api/servers/${id}/restart`, mutation("POST", {}), token),
  killServer: (token: string, id: string) =>
    request<{ ok: boolean }>(`/api/servers/${id}/kill`, mutation("POST", {}), token),
  deleteServer: (token: string, id: string) =>
    request<{ ok: boolean }>(`/api/servers/${id}`, mutation("DELETE"), token),
  telemetry: (token: string, id: string, signal?: AbortSignal) =>
    request<{ stale: boolean; telemetry: ServerTelemetry | null }>(
      `/api/servers/${id}/telemetry`,
      { signal },
      token,
    ),
  players: (token: string, id: string, signal?: AbortSignal) =>
    request<ServerPlayers>(`/api/servers/${id}/players`, { signal }, token),
  kickPlayer: (token: string, id: string, playerName: string, reason?: string) =>
    request<{ serverId: string; playerName: string; kicked: boolean }>(
      `/api/servers/${id}/players/${encodeURIComponent(playerName)}/kick`,
      mutation("POST", reason?.trim() ? { reason: reason.trim() } : {}),
      token,
    ),
  files: (token: string, id: string, path = "", signal?: AbortSignal) => {
    const query = new URLSearchParams({ path });
    return request<FileListResponse>(`/api/servers/${id}/files?${query}`, { signal }, token);
  },
  fileContent: (
    token: string,
    id: string,
    path: string,
    offset = 0,
    limit = 64 * 1024,
    signal?: AbortSignal,
  ) => {
    const query = new URLSearchParams({ path, offset: String(offset), limit: String(limit) });
    return request<FileContentResponse>(
      `/api/servers/${id}/files/content?${query}`,
      { signal },
      token,
    );
  },
  writeFile: (
    token: string,
    id: string,
    body: { path: string; content: string; revision: FileRevision | null },
  ) => request<FileWriteResponse>(`/api/servers/${id}/files/content`, mutation("PUT", body), token),
  uploadFile: (token: string, id: string, path: string, file: File) => {
    const body = new FormData();
    body.append("path", path);
    body.append("file", file, file.name);
    return request<{ path: string; size: number }>(
      `/api/servers/${id}/files/upload`,
      { method: "POST", body },
      token,
    );
  },
  downloadFile: (token: string, id: string, path: string) => {
    const query = new URLSearchParams({ path });
    return request<FileDownloadResponse>(`/api/servers/${id}/files/download?${query}`, {}, token);
  },
  createDirectory: (token: string, id: string, path: string) =>
    request<{ path: string }>(
      `/api/servers/${id}/files/directories`,
      mutation("POST", { path }),
      token,
    ),
  extractArchive: (token: string, id: string, path: string, destination: string) =>
    request<{ path: string; files: number; bytes: number }>(
      `/api/servers/${id}/files/extract`,
      mutation("POST", { path, destination }),
      token,
    ),
  moveFile: (token: string, id: string, from: string, to: string) =>
    request<{ path: string }>(
      `/api/servers/${id}/files/move`,
      mutation("POST", { from, to }),
      token,
    ),
  deleteFile: (token: string, id: string, path: string) => {
    const query = new URLSearchParams({ path });
    return request<{ path: string }>(
      `/api/servers/${id}/files?${query}`,
      mutation("DELETE"),
      token,
    );
  },
  upgradeResource: (token: string, id: string, resource: ServerResource) =>
    request<{
      resource: ServerResource;
      value: number;
      cost: number;
      stepLabel: string;
      label: string;
    }>(`/api/servers/${id}/resources/${resource}/upgrade`, mutation("POST", {}), token),
  adminNodes: (token: string, options: AdminInventoryQuery = {}, signal?: AbortSignal) =>
    request<AdminPage<AdminNode>>(`/api/admin/nodes?${inventoryQuery(options)}`, { signal }, token),
  adminNode: (token: string, id: string, signal?: AbortSignal) =>
    request<AdminNode>(`/api/admin/nodes/${id}`, { signal }, token),
  adminAudit: (token: string, signal?: AbortSignal) =>
    request<NodeAuditEvent[]>("/api/admin/nodes/audit", { signal }, token),
  adminOverview: (token: string, signal?: AbortSignal) =>
    request<AdminOverview>("/api/admin/overview", { signal }, token),
  adminStats: (token: string, days = 30, signal?: AbortSignal) =>
    request<AdminStats>(`/api/admin/stats?days=${days}`, { signal }, token),
  adminUsers: (token: string, query = "", signal?: AbortSignal, cursor: string | null = null) =>
    request<AdminPage<AdminUser>>(
      `/api/admin/users?query=${encodeURIComponent(query)}&limit=10${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ""}`,
      { signal },
      token,
    ),
  adminUser: (token: string, id: string, signal?: AbortSignal) =>
    request<AdminUserDetails>(`/api/admin/users/${id}`, { signal }, token),
  adminUserLedger: (
    token: string,
    id: string,
    cursor: string | null = null,
    limit = 10,
    signal?: AbortSignal,
  ) =>
    request<AdminUserLedgerResult>(
      `/api/admin/users/${id}/ledger?limit=${limit}${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ""}`,
      { signal },
      token,
    ),
  adminUserServers: (
    token: string,
    id: string,
    cursor: string | null = null,
    signal?: AbortSignal,
  ) =>
    request<AdminPage<AdminUserDetails["servers"][number]>>(
      `/api/admin/users/${id}/servers?limit=10${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ""}`,
      { signal },
      token,
    ),
  adminSetRole: (token: string, id: string, role: string) =>
    request<{ id: string; username: string; role: string }>(
      `/api/admin/users/${id}/role`,
      mutation("POST", { role }),
      token,
    ),
  adminAdjustCredits: (token: string, id: string, amount: number, reason: string) =>
    request<{ balance: number; amount: number }>(
      `/api/admin/users/${id}/credits`,
      mutation("POST", { amount, reason }),
      token,
    ),
  adminSetServerLimit: (token: string, id: string, serverLimit: number) =>
    request<{ id: string; username: string; serverLimit: number }>(
      `/api/admin/users/${id}/server-limit`,
      mutation("POST", { serverLimit }),
      token,
    ),
  adminPayments: (
    token: string,
    query = "",
    status = "",
    signal?: AbortSignal,
    cursor: string | null = null,
  ) =>
    request<AdminPage<AdminPayment>>(
      `/api/admin/payments?query=${encodeURIComponent(query)}&status=${encodeURIComponent(status)}&limit=20${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ""}`,
      { signal },
      token,
    ),
  adminCheckPayment: (token: string, id: string, signal?: AbortSignal) =>
    request<AdminPayment>(
      `/api/admin/payments/${encodeURIComponent(id)}/check`,
      { ...mutation("POST", {}), signal },
      token,
    ),
  adminServers: (
    token: string,
    query = "",
    status = "",
    signal?: AbortSignal,
    cursor: string | null = null,
  ) =>
    request<AdminPage<AdminServer>>(
      `/api/admin/servers?query=${encodeURIComponent(query)}&status=${encodeURIComponent(status)}&limit=10${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ""}`,
      { signal },
      token,
    ),
  adminServer: (token: string, id: string, signal?: AbortSignal) =>
    request<AdminServer & { diskLimitMb: number; playerLimit: number; cpuLimitPercent: number }>(
      `/api/admin/servers/${id}`,
      { signal },
      token,
    ),
  adminUpdateServer: (
    token: string,
    id: string,
    body: {
      name?: string;
      core?: string;
      version?: string;
      memLimitMb?: number;
      diskLimitMb?: number;
      cpuLimitPercent?: number;
      playerLimit?: number;
      nodeId?: string | null;
      confirmWipe?: boolean;
      reinstall?: boolean;
    },
  ) => request<AdminServer>(`/api/admin/servers/${id}`, mutation("PATCH", body), token),
  adminStartServer: (token: string, id: string) =>
    request<{ ok: boolean }>(`/api/admin/servers/${id}/start`, mutation("POST", {}), token),
  adminRestartServer: (token: string, id: string) =>
    request<{ ok: boolean }>(`/api/admin/servers/${id}/restart`, mutation("POST", {}), token),
  adminStopServer: (token: string, id: string) =>
    request<{ ok: boolean }>(`/api/admin/servers/${id}/stop`, mutation("POST", {}), token),
  adminKillServer: (token: string, id: string) =>
    request<{ ok: boolean }>(`/api/admin/servers/${id}/kill`, mutation("POST", {}), token),
  adminDeleteServer: (token: string, id: string) =>
    request<{ ok: boolean }>(`/api/admin/servers/${id}/delete`, mutation("POST", {}), token),
  adminFullAudit: (token: string, signal?: AbortSignal) =>
    request<AdminAuditEvent[]>("/api/admin/audit?limit=200", { signal }, token),
  createNode: (
    token: string,
    body: {
      name: string;
      hostname?: string;
      publicIp?: string;
      region?: string;
      capacityContainers?: number;
      maxRunningMemoryMb?: number | null;
    },
  ) =>
    request<{ node: { id: string; name: string }; token: string }>(
      "/api/admin/nodes",
      mutation("POST", body),
      token,
    ),
  updateNode: (
    token: string,
    id: string,
    body: {
      name?: string;
      hostname?: string;
      publicIp?: string;
      region?: string;
      capacityContainers?: number;
      maxRunningMemoryMb?: number | null;
    },
  ) => request<AdminNode>(`/api/admin/nodes/${id}`, mutation("PATCH", body), token),
  nodeServers: (
    token: string,
    id: string,
    options: AdminInventoryQuery = {},
    signal?: AbortSignal,
  ) =>
    request<AdminPage<NodeServer>>(
      `/api/admin/nodes/${id}/servers?${inventoryQuery(options)}`,
      { signal },
      token,
    ),
  rotateNodeToken: (token: string, id: string) =>
    request<{ node: { id: string; name: string }; token: string }>(
      `/api/admin/nodes/${id}/rotate`,
      mutation("POST", {}),
      token,
    ),
  revokeNodeToken: (token: string, id: string) =>
    request<{ ok: boolean }>(`/api/admin/nodes/${id}/revoke`, mutation("POST", {}), token),
  setNodeStatus: (token: string, id: string, action: "drain" | "disable" | "enable") =>
    request<AdminNode>(`/api/admin/nodes/${id}/${action}`, mutation("POST", {}), token),
  backups: (token: string, id: string, signal?: AbortSignal) =>
    request<Backup[]>(`/api/servers/${id}/backups`, { signal }, token),
  createBackup: (token: string, id: string) =>
    request<{ id: string; status: string }>(
      `/api/servers/${id}/backups`,
      mutation("POST", {}),
      token,
    ),
  restoreBackup: (token: string, id: string, backupId: string) =>
    request<{ id: string; status: string }>(
      `/api/servers/${id}/backups/${backupId}/restore`,
      mutation("POST", {}),
      token,
    ),
  downloadBackup: (token: string, id: string, backupId: string) =>
    request<{ url: string }>(`/api/servers/${id}/backups/${backupId}/download`, {}, token),
  deleteBackup: (token: string, id: string, backupId: string) =>
    request<{ ok: boolean }>(`/api/servers/${id}/backups/${backupId}`, mutation("DELETE"), token),
  purchaseBackupSlot: (token: string, id: string) =>
    request<{ balance: number; backupSlots: number; backupsEnabled: boolean; cost: number }>(
      `/api/servers/${id}/backups/slot`,
      mutation("POST", {}),
      token,
    ),
};
